/** entrypoint for front-end application. */
import type { RumEvent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { defaultTheme, ThemeProvider } from '@ksyos/design-system';
import 'core-js/stable';
import { TextDecoder } from 'fastestsmallesttextencoderdecoder';
import 'regenerator-runtime/runtime';
import '@common/utils/consoleExtension';
import './create-nonce';
if (!window['TextDecoder']) {
    window['TextDecoder'] = TextDecoder;
}
import { createBrowserRouter, RouterProvider } from '@ksyos/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import type { DatadogSettings, FrontendSettings } from '@common/interfaces/frontendSettings';
import { loadGlobalize } from '../common/translate/globalize';
import { App } from './components/App';
import { store } from './configureStore';
import { nonProductionRoutes, routes } from './routes';
import { getFrontendSettings } from './services/api/frontendSettings';
import { FrontendSettingsProvider } from './utils/hooks/useFrontendSettings';

function isIgnorableDatadogRumEvent(event: RumEvent) {
    const isAbortError = event.type === 'error' && event.error.type === 'AbortError';
    const isTimeoutError = event.type === 'error' && event.error.type === 'TimeoutError';

    return isAbortError || isTimeoutError;
}

function configureAndStartDatadogRum({
    isEnabled,
    applicationId,
    clientToken,
    env,
    allowedTracingUrls,
    trackLongTasks,
    trackResources,
    trackUserInteractions,
    version,
}: DatadogSettings) {
    if (isEnabled === false) {
        return;
    }

    datadogRum.init({
        allowedTracingUrls: allowedTracingUrls.split(','),
        applicationId,
        beforeSend: (event, _context) => {
            return !isIgnorableDatadogRumEvent(event);
        },
        clientToken,
        defaultPrivacyLevel: 'mask',
        enablePrivacyForActionName: true,
        env,
        service: 'clientsafeweb',
        sessionPersistence: 'local-storage',
        sessionReplaySampleRate: 100,
        sessionSampleRate: 100,
        site: 'datadoghq.eu',
        trackLongTasks,
        trackResources,
        trackUserInteractions,
        trackViewsManually: true,
        useSecureSessionCookie: true,
        version,
    });

    datadogRum.startSessionReplayRecording();
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
        },
    },
});

async function startApplication() {
    const ksyosSettings = await getFrontendSettings();
    const { datadogSettings } = ksyosSettings;

    configureAndStartDatadogRum(datadogSettings);

    const rootNode = document.getElementById('app');

    if (rootNode) {
        loadGlobalize();
        const root = createRoot(rootNode);
        root.render(
            <>
                {process.env.REACT_ENABLE_STRICTMODE === 'true' ? (
                    <StrictMode>
                        <FrontendSettingsProvider settings={ksyosSettings}>
                            <ClientsafeRouter ksyosSettings={ksyosSettings} />
                        </FrontendSettingsProvider>
                    </StrictMode>
                ) : (
                    <FrontendSettingsProvider settings={ksyosSettings}>
                        <ClientsafeRouter ksyosSettings={ksyosSettings} />
                    </FrontendSettingsProvider>
                )}
            </>
        );
    }
}

// Can be simplified when class-components are removed
// then we no longer need to to pass the ksyosSettings through the router
function makeRouter(ksyosSettings: FrontendSettings) {
    const childRoutes = [...routes];

    if (['development', 'staging'].includes(ksyosSettings.environment)) {
        routes.push(...nonProductionRoutes);
    }

    return createBrowserRouter([
        {
            children: childRoutes,
            element: <App />,
            path: '',
        },
    ]);
}

function ClientsafeRouter({ ksyosSettings }: { ksyosSettings: FrontendSettings }) {
    return (
        <Provider store={store}>
            <ThemeProvider theme={defaultTheme}>
                <QueryClientProvider client={queryClient}>
                    <RouterProvider router={makeRouter(ksyosSettings)} />
                </QueryClientProvider>
            </ThemeProvider>
        </Provider>
    );
}

startApplication().catch((error) => {
    throw error;
});
